import { type ReactElement, useCallback, useEffect, useState } from 'react';
import { type FoodMerchantView, type StoreOrderabilityView, type StoreProfileView } from '@vello/api-client';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, Drawer, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, type Column, DrawerHeader, DrawerBody, DrawerFooter, SectionTitle, Stack, Text, Spacer } from '@vello/web-theme';
import {
  STORE_PAUSE_MINUTES,
  STORE_PAUSE_REASONS,
  formatMarketInstant,
  mayOperateStores,
  minuteOfDayToClock,
  storeOrderableReason,
  storeOrderableTone,
  storePauseExpiry,
  weeklyWindowParts,
} from '../../src/index';
import { useAdmin } from '../session';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { DrawerStack } from '../components/drawer-stack';

/**
 * Food › Lojas (W6-ADMIN-SCREENS, D401) — the **operations** view of the storefront operating profile
 * (`/v1/store-profile/*`). W6-MERCHANT-STORE (D394) gave the merchant its own "Loja" screen; this is the side the
 * platform works from: which stores of a merchant are published, whether each is open, busy or paused, the weekly
 * hours it keeps, and the pause / resume an operator runs when a store stops answering.
 *
 * A pause always carries a **reason code and an expiry** — the server's contract — so the screen offers a closed
 * reason list and a duration instead of a free-text instant, and the pause lapses on its own. Hours are minute-of-week
 * windows in the store's own clock (the profile names its timezone), so they are rendered as a weekday plus a 24-hour
 * clock, never through a market locale that would shift them.
 */
export function StoreStatusScreen(): ReactElement {
  const { runtime, grants, markets, scope } = useAdmin();
  const { toast, confirm } = useConsole();
  const { t, lang } = useT();
  const { v, fail } = useVocab();
  const weekday = (n: number): string => v('time.weekday', (['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const)[n]);
  const mayOperate = mayOperateStores(grants);

  const [merchants, setMerchants] = useState<readonly FoodMerchantView[] | undefined>(undefined);
  const [merchantId, setMerchantId] = useState<string | undefined>(undefined);
  const [profiles, setProfiles] = useState<readonly StoreProfileView[] | undefined>(undefined);
  // D849 (P0-012): the server's orderability decision per store (what a rider is served), keyed by location. A missing entry = not read.
  const [decisions, setDecisions] = useState<Readonly<Record<string, StoreOrderabilityView>>>({});
  const [listError, setListError] = useState<string | undefined>(undefined);
  const [open, setOpen] = useState<StoreProfileView | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | undefined>(undefined);
  const [pauseMinutes, setPauseMinutes] = useState<number>(STORE_PAUSE_MINUTES[1] as number);

  useEffect(() => {
    void (async () => {
      const res = await runtime.foodMerchant.listMerchants();
      if (res.ok) {
        setMerchants(res.value.merchants);
        if (res.value.merchants.length === 1) setMerchantId(res.value.merchants[0]?.merchantId);
      } else {
        setMerchants([]);
        setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
      }
    })();
  }, [runtime, t]);

  const load = useCallback(async (): Promise<void> => {
    if (merchantId === undefined) {
      setProfiles(undefined);
      return;
    }
    const res = await runtime.storeProfile.listByMerchant(merchantId);
    if (res.ok) {
      const rows = res.value.profiles;
      // One decision per store from the same route the merchant's console reads; a failed read leaves that store "not verified", never "open".
      const answers = await Promise.all(rows.map((r) => runtime.storeProfile.orderability(r.locationId)));
      const next: Record<string, StoreOrderabilityView> = {};
      answers.forEach((a, i) => {
        const row = rows[i];
        if (a.ok && row !== undefined) next[row.locationId] = a.value;
      });
      setDecisions(next);
      setProfiles(rows);
      setListError(undefined);
    } else {
      setProfiles([]);
      setListError(res.error.code === 'FORBIDDEN' ? t('w6.refused.forbidden') : t('w6.refused', { code: fail(res.error) }));
    }
  }, [runtime, merchantId, t]);

  useEffect(() => {
    setProfiles(undefined);
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.queue, enabled: merchantId !== undefined });

  const pause = async (record: StoreProfileView): Promise<void> => {
    const answer = await confirm({
      title: t('stores.pause.title', { store: record.profile.displayName }),
      body: t('stores.pause.body'),
      confirmLabel: t('stores.pause.confirm'),
      cancelLabel: t('ui.cancel'),
      tone: 'danger',
      icon: 'pause',
      // The chips carry BOTH choices the server needs: the reason and how long the pause lasts.
      reasons: STORE_PAUSE_REASONS.map((code) => v('food.store_pause_reason', code)),
      reasonLabel: t('w6.reason.label'),
      reasonsOnly: true,
    });
    if (!answer.confirmed || answer.reason === undefined) return;
    const reasonCode = STORE_PAUSE_REASONS.find((code) => v('food.store_pause_reason', code) === answer.reason);
    if (reasonCode === undefined) return;
    const expiresAt = storePauseExpiry(new Date().toISOString(), pauseMinutes);
    if (expiresAt === undefined) return;
    setBusy(true);
    setActionError(undefined);
    const res = await runtime.storeProfile.pause(record.locationId, reasonCode, expiresAt);
    setBusy(false);
    if (res.ok) {
      setOpen(res.value.record);
      toast({ message: t('stores.pause.done'), tone: 'success' });
      await load();
      return;
    }
    setActionError(t('w6.actionRefused', { code: fail(res.error) }));
  };

  const resume = async (record: StoreProfileView): Promise<void> => {
    setBusy(true);
    setActionError(undefined);
    const res = await runtime.storeProfile.resume(record.locationId);
    setBusy(false);
    if (res.ok) {
      setOpen(res.value.record);
      toast({ message: t('stores.resume.done'), tone: 'success' });
      await load();
      return;
    }
    setActionError(t('w6.actionRefused', { code: fail(res.error) }));
  };

  /** The orderable state of a store, worded: the badge text (the gate's reason) and the sentence that says why. */
  const orderable = (r: StoreProfileView): { readonly tone: ReturnType<typeof storeOrderableTone>; readonly label: string; readonly why: string } => {
    const decision = decisions[r.locationId];
    const code = storeOrderableReason(r, decision) ?? 'UNKNOWN';
    const known = `stores.orderable.${code}`;
    // A code the catalog does not word yet is shown as the code itself, never as "open".
    const has = (id: string): boolean => t(id) !== id;
    return {
      tone: storeOrderableTone(decision),
      label: has(known) ? t(known) : code,
      why: has(`${known}.why`) ? t(`${known}.why`) : code,
    };
  };

  const columns: readonly Column<StoreProfileView>[] = [
    { key: 'store', header: t('stores.col.store'), width: 'minmax(160px, 1.4fr)', render: (r) => r.profile.displayName },
    { key: 'publication', header: t('stores.col.publication'), width: 'minmax(120px, 0.9fr)', render: (r) => v('merchant.store_profile_status', r.profile.status) },
    {
      key: 'availability',
      header: t('stores.col.availability'),
      width: 'minmax(150px, 1.1fr)',
      render: (r) => <Badge tone={orderable(r).tone}>{orderable(r).label}</Badge>,
    },
    {
      key: 'capacity',
      header: t('stores.col.capacity'),
      width: 'minmax(140px, 1fr)',
      render: (r) => (r.capacity === undefined ? '—' : <Text role="caption" tone="secondary">{t('stores.capacity', { load: String(r.capacity.currentLoad), max: String(r.capacity.maxConcurrent) })}</Text>),
    },
  ];

  const windows = open?.hours?.regular ?? [];
  const scopeZone = scope.current.city !== undefined ? markets.find((m) => m.id === scope.current.city)?.timezone : undefined;

  return (
    <>
      <PageHeader
        title={t('stores.title')}
        subtitle={t('stores.subtitle')}
      />

      <Panel>
        <Stack gap={4}>
          <Field label={t('stores.field.merchant')} htmlFor="stores-merchant" hint={t('stores.needsMerchant.detail')}>
            <Select id="stores-merchant" value={merchantId ?? ''} onChange={(e) => setMerchantId(e.target.value === '' ? undefined : e.target.value)}>
              <option value="">{t('stores.merchant.choose')}</option>
              {(merchants ?? []).map((m) => (
                <option key={m.merchantId} value={m.merchantId}>{m.legalEntityRef}</option>
              ))}
            </Select>
          </Field>
        </Stack>
      </Panel>


      {merchantId === undefined ? (
        <Panel>
          <EmptyState title={t((merchants ?? []).length === 0 ? 'stores.noMerchants' : 'stores.needsMerchant.title')} {...((merchants ?? []).length === 0 ? {} : { detail: t('stores.needsMerchant.detail') })} />
        </Panel>
      ) : (
        <ListTable
          columns={columns}
          rows={profiles ?? []}
          rowKey={(r) => r.locationId}
          onRowClick={(r) => { setOpen(r); setActionError(undefined); }}
          selectedKey={open?.locationId}
          loading={profiles === undefined}
          loadingLabel={t('ui.loading')}
          error={listError}
          onRetry={() => void load()}
          retryLabel={t('ui.retry')}
          empty={<EmptyState title={t('stores.empty.title')} detail={t('stores.empty.detail')} />}
          minWidth={900}
        />
      )}

      <Drawer open={open !== undefined} onClose={() => setOpen(undefined)} labelledBy="stores-detail-title">
        {open !== undefined && (
          <>
            <DrawerHeader titleId="stores-detail-title" title={open.profile.displayName} subtitle={<Badge tone={orderable(open).tone}>{orderable(open).label}</Badge>} />
            <DrawerBody>
              <DrawerStack>
                {!mayOperate && <Notice tone="neutral">{t('w6.noPermission.action')}</Notice>}
                {actionError !== undefined && <Notice tone="danger">{actionError}</Notice>}
                <Notice tone={orderable(open).tone === 'success' ? 'neutral' : 'warning'}>
                  {orderable(open).why}
                  {decisions[open.locationId]?.opensAt != null && (
                    <>
                      {' '}
                      {t('stores.orderable.opensAt', { when: formatMarketInstant(decisions[open.locationId]?.opensAt ?? undefined, decisions[open.locationId]?.timezone ?? open.profile.timezoneRef, lang) })}
                    </>
                  )}
                </Notice>
                <Text as="p" role="caption" tone="secondary">{t('stores.col.availability.merchantSwitch', { mode: v('merchant.store_availability_mode', open.availability.mode) })}</Text>
                {open.availability.pause !== undefined && (
                  <Notice tone="warning">
                    {t('stores.pausedUntil', {
                      when: formatMarketInstant(open.availability.pause.expiresAt, open.profile.timezoneRef, lang),
                      reason: v('food.store_pause_reason', open.availability.pause.reasonCode),
                    })}
                  </Notice>
                )}

                <section>
                  <SectionTitle level={3} size="sub">{t('stores.hours.title')}</SectionTitle>
                  <Text as="p" role="caption" tone="secondary">{t('stores.hours.zone', { tz: open.profile.timezoneRef })}</Text>
                  {windows.length === 0 ? (
                    <Text as="p" role="caption" tone="secondary">{t('stores.hours.empty')}</Text>
                  ) : (
                    windows.map((w, i) => {
                      const parts = weeklyWindowParts(w);
                      if (parts === undefined) return null;
                      return (
                        <Text as="div" role="caption" mono key={`${w.startMinuteOfWeek}-${w.endMinuteOfWeek}-${i}`}>
                          {t('stores.hours.window', {
                            startDay: weekday(parts.startDay),
                            startTime: minuteOfDayToClock(parts.startMinuteOfDay),
                            endDay: weekday(parts.endDay),
                            endTime: minuteOfDayToClock(parts.endMinuteOfDay),
                          })}
                        </Text>
                      );
                    })
                  )}
                </section>

                {mayOperate && open.availability.mode !== 'paused' && (
                  <Field label={t('stores.pause.minutes')} htmlFor="stores-pause-minutes">
                    <Select id="stores-pause-minutes" value={String(pauseMinutes)} onChange={(e) => setPauseMinutes(Number(e.target.value))}>
                      {STORE_PAUSE_MINUTES.map((m) => (
                        <option key={m} value={String(m)}>{t('stores.pause.minutesValue', { n: String(m) })}</option>
                      ))}
                    </Select>
                  </Field>
                )}
                {scopeZone !== undefined && open.profile.timezoneRef !== scopeZone && <Text as="p" role="caption" tone="secondary">{t('stores.hours.zone', { tz: open.profile.timezoneRef })}</Text>}
              </DrawerStack>
            </DrawerBody>
            <DrawerFooter>
              <Button variant="ghost" onClick={() => setOpen(undefined)}>{t('ui.close')}</Button>
              <Spacer />
              {mayOperate &&
                (open.availability.mode === 'paused' ? (
                  <Button variant="primary" icon="rotateCcw" disabled={busy} onClick={() => void resume(open)}>{t('stores.resume.action')}</Button>
                ) : (
                  <Button variant="danger" icon="pause" disabled={busy} onClick={() => void pause(open)}>{t('stores.pause.action')}</Button>
                ))}
            </DrawerFooter>
          </>
        )}
      </Drawer>
    </>
  );
}
